import {
  RefreshCw,
  CheckCircle,
  XCircle,
  X,
  Server,
} from 'lucide-react';
import type { ExecutionJob } from '../../api/coder';

interface ExecutionDetailSidebarProps {
  execution: ExecutionJob;
  onClose: () => void;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'COMPLETED': return 'bg-accent-100 text-accent-700';
    case 'RUNNING': return 'bg-coral-100 text-coral-700';
    case 'FAILED': return 'bg-red-100 text-red-700';
    default: return 'bg-neutral-100 text-neutral-600';
  }
}

export function ExecutionDetailSidebar({ execution, onClose }: ExecutionDetailSidebarProps) {
  return (
    <div className="w-[480px] border-l border-neutral-200 overflow-y-auto bg-neutral-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-neutral-800 uppercase tracking-wider">
            Execution Details
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>
        <p className="text-[11px] font-mono text-neutral-500 mt-0.5">
          {execution.batchId}
        </p>
      </div>

      <div className="p-4 space-y-3">
        {/* Status */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-neutral-100">
            <h3 className="text-xs font-bold text-neutral-800 uppercase tracking-widest flex items-center gap-2">
              <span className="w-1 h-4 bg-coral-400 rounded-full" />
              Status
            </h3>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-2">
              {execution.status === 'RUNNING' && (
                <RefreshCw className="w-4 h-4 animate-spin text-coral-500" />
              )}
              {execution.status === 'COMPLETED' && <CheckCircle className="w-4 h-4 text-accent-500" />}
              {execution.status === 'FAILED' && <XCircle className="w-4 h-4 text-red-500" />}
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusBadgeClass(execution.status)}`}>
                {execution.status}
              </span>
            </div>
            {execution.progress && (
              <div className="mt-3">
                <p className="text-xs text-neutral-600 mb-1.5">
                  {execution.progress.message}
                </p>
                <div className="h-1.5 rounded-full overflow-hidden bg-neutral-100">
                  <div
                    className="h-full rounded-full bg-coral-500"
                    style={{ width: `${execution.progress.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        {execution.executionState && (
          <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-neutral-100">
              <h3 className="text-xs font-bold text-neutral-800 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1 h-4 bg-coral-400 rounded-full" />
                Progress
              </h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Epics</p>
                <p className="text-sm font-medium text-neutral-800 font-mono">
                  {execution.executionState.epicsCompleted}/{execution.executionState.epicsTotal}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Stories</p>
                <p className="text-sm font-medium text-neutral-800 font-mono">
                  {execution.executionState.storiesCompleted}/{execution.executionState.storiesTotal}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Successful</p>
                <p className="text-sm font-medium text-accent-600 font-mono">
                  {execution.executionState.storiesSuccessful}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Failed</p>
                <p className="text-sm font-medium text-red-600 font-mono">
                  {execution.executionState.storiesFailed}
                </p>
              </div>
            </div>
            {execution.executionState.activeSprites > 0 && (
              <div className="mx-4 mb-3 pt-3 border-t border-neutral-100">
                <p className="text-xs text-neutral-600 flex items-center gap-1.5">
                  <Server className="w-3.5 h-3.5 text-coral-400" />
                  {execution.executionState.activeSprites} active sprites
                </p>
              </div>
            )}
          </div>
        )}

        {/* Result */}
        {execution.result && (
          <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-neutral-100">
              <h3 className="text-xs font-bold text-neutral-800 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1 h-4 bg-coral-400 rounded-full" />
                Result
              </h3>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600">Success</span>
                <span className={execution.result.success ? 'text-accent-600 font-medium' : 'text-red-600 font-medium'}>
                  {execution.result.success ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600">Duration</span>
                <span className="text-neutral-800 font-mono">
                  {Math.round(execution.result.duration / 1000)}s
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600">Sprites Used</span>
                <span className="text-neutral-800 font-mono">{execution.result.summary.spritesUsed}</span>
              </div>
            </div>
          </div>
        )}

        {/* Errors */}
        {execution.executionState?.errors && execution.executionState.errors.length > 0 && (
          <div className="bg-white rounded-lg border border-red-200 shadow-warm-sm overflow-hidden">
            <div className="px-4 py-2.5 border-b border-red-100">
              <h3 className="text-xs font-bold text-red-700 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1 h-4 bg-red-400 rounded-full" />
                Errors
              </h3>
            </div>
            <div className="p-4">
              <ul className="space-y-1.5">
                {execution.executionState.errors.map((error, idx) => (
                  <li key={idx} className="text-xs text-red-600">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
